import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import {
  getAllPostsWithSlug,
  getCategoriesFivePostWithSlug,
  getCategoriesFivePostWithSlugByID,
  getPostAndMorePosts,
} from "../../lib/api";
import Layout from "../../components/layout";
import Head from "next/head";
import ErrorPage404 from "../../components/404";
import PostBody from "../../components/post-body";
import { mwImage } from "../../lib/common";
import BlogAuthorHero from "../../components/v2/blog/blog-author-hero";
import BlogPostHero from "../../components/v2/blog/blog-post-hero";
import BlogScrollNav from "../../components/v2/blog/blog-scroll-nav";
import BlogStickyCta from "../../components/v2/blog/blog-sticky-cta";
import BlogScrollTop from "../../components/v2/blog/blog-scroll-top";
import CategoryCustomPosts from "../../components/v2/blog/categories-posts-pages";
import RelatedPosts from "../../components/v2/blog/related-posts";
import {
  getLegacyBlogPostRedirectDestination,
  isLegacyBlogPostSlug,
} from "../../lib/legacyBlogRedirects";
import { extractFaqSchemaFromHtml } from "../../lib/faqSchema";
import "../../styles/scss/blog.module.scss";
import "../../styles/scss/customer.module.scss";
import "../../styles/scss/single-blog-v3.module.scss";

function slugifyHeading(title) {
  return title
    .replace(/[^\w\s]/gi, "")
    .replace(/\s+/g, "-")
    .toLowerCase();
}

function isHubCategoryPage(slug) {
  return (
    slug === "observability" ||
    slug === "aws-monitoring" ||
    slug === "kubernetes-monitoring"
  );
}

export default function Post({ post, allCatPosts, allCategoriesData }) {
  const router = useRouter();
  const [headings, setHeadings] = useState([]);
  const isHub = isHubCategoryPage(post?.slug);

  const postContent = useMemo(
    () =>
      post?.content?.replace(
        /https:\/\/(middleware\.io\/backend|wpadmin\.middleware\.io)\/wp-content/g,
        "https://middleware.io/wp-content"
      ),
    [post?.content]
  );

  const featuredImage = mwImage(post?.featuredImage?.node?.sourceUrl);

  useEffect(() => {
    if (!post?.slug) return;

    const sourceElement = document.querySelector("#sticky-banner");
    const targetDiv = document.querySelector("#sticky");
    if (sourceElement && targetDiv) {
      targetDiv.appendChild(sourceElement);
    }
    return () => {
      const originalLocation = document.querySelector("#parent-banner");
      if (sourceElement && originalLocation) {
        originalLocation.appendChild(sourceElement);
      }
    };
  }, [post?.slug]);

  useEffect(() => {
    if (typeof document === "undefined" || !post?.slug) return;

    const elements = document.querySelectorAll(".main-content .post-body h2");
    const nextHeadings = [];

    elements.forEach((item) => {
      if (item.tagName !== "H2") return;
      const title = item.textContent.trim();
      if (!title) return;
      const id = slugifyHeading(title);
      item.setAttribute("id", id);

      if (!item.querySelector(":scope > a.anchor-link")) {
        item.innerHTML = `
          <a aria-label="Link to section"
             class="anchor-link"
             href="#${id}">
             ${title}
          </a>
        `;
      }

      nextHeadings.push({ id, title });
    });

    setHeadings(nextHeadings);

    const Faqaccordion = document.querySelectorAll(".schema-faq-section");
    const faqHandler = (e) => {
      e.currentTarget.classList.toggle("active");
    };
    Faqaccordion.forEach((child) => {
      child.addEventListener("click", faqHandler);
    });

    const setupAccordion = () => {
      const faq_accordion = document.getElementsByClassName("sc_fs_faq");
      if (faq_accordion.length > 0) {
        for (let i = 0; i < faq_accordion.length; i++) {
          if (i === 0) {
            faq_accordion[i].classList.add("active");
          }
          faq_accordion[i].addEventListener("click", (event) => {
            for (let j = 0; j < faq_accordion.length; j++) {
              faq_accordion[j].classList.remove("active");
            }
            event.currentTarget.classList.add("active");
          });
        }
      }
    };

    setupAccordion();
    const observer = new MutationObserver(() => {
      setupAccordion();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      Faqaccordion.forEach((child) => {
        child.removeEventListener("click", faqHandler);
      });
    };
  }, [postContent, post?.slug]);

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  if (!post?.slug) {
    return <ErrorPage404 />;
  }

  return (
    <>
      {featuredImage ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={featuredImage}
            fetchPriority="high"
          />
        </Head>
      ) : null}
      <Layout
        ogImage={featuredImage}
        isHomePage={false}
        pageTitle={post?.title}
        uri={`/blog/${post?.slug?.replace(/^\/|\/$/g, "")}/`}
        metaTitle={post?.seo?.title}
        metaDesc={post?.seo?.metaDesc}
        metaRobotsNoindex={post?.seo?.metaRobotsNoindex}
        metaRobotsNofollow={post?.seo?.metaRobotsNofollow}
      >
        <div className={"middle-wrapper site-page"}>
          <section className={"single-blog-v3"}>
            <div className="blog-post-shell">
              <BlogPostHero post={post} />
            </div>

            <div className="blog-article-layout">
              <BlogStickyCta />
              <div className="blog-article-column main-content">
                <PostBody content={postContent} />
                <div id="sticky" />
              </div>
              <BlogScrollNav headings={headings} />
              <BlogScrollTop />
              <BlogAuthorHero post={post} />
            </div>
          </section>

          {isHub ? (
            <CategoryCustomPosts
              category=""
              selectedBlog={post}
              data={allCategoriesData}
            />
          ) : (
            <RelatedPosts
              categories={(post?.categories?.edges ?? []).map((e) => e.node.name)}
              selectedBlog={post}
              data={allCatPosts}
            />
          )}
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps = async ({ params, preview = false, previewData, locale }) => {
  const legacyDestination = getLegacyBlogPostRedirectDestination(params?.slug);
  if (legacyDestination) {
    return {
      redirect: { destination: legacyDestination, permanent: true },
    };
  }

  let postIds = [];
  if (params?.slug === "observability") {
    postIds = [1648, 4387, 4523, 4471, 7303];
  } else if (params?.slug === "aws-monitoring") {
    postIds = [5258, 4567, 5386, 1678];
  } else if (params?.slug === "kubernetes-monitoring") {
    postIds = [4066, 1745, 4647];
  }

  const currentLocale = locale ?? "en";
  const allCategoriesData = await getCategoriesFivePostWithSlugByID(postIds);

  const data = await getPostAndMorePosts(params?.slug, preview, previewData, currentLocale);
  const post = data?.post;

  if (!post?.slug) {
    return { notFound: true };
  }

  const allCatPosts = await getCategoriesFivePostWithSlug();
  const sanitizeSlug = (slug) => slug?.replace(/^\/|\/$/g, "") || "";
  const postUri = `/blog/${sanitizeSlug(post.slug)}/`;
  const faqSchema = extractFaqSchemaFromHtml(post?.content) || null;

  return {
    props: {
      preview,
      post,
      posts: data?.posts,
      allCatPosts: allCatPosts,
      allCategoriesData: allCategoriesData,
      pageTitle:
        post?.seo?.title ||
        "Observability News, and Best Practices and Guides",
      description:
        post?.seo?.metaDesc ||
        "Read how observability can scale your cloud infrastructure, best practices, how-tos guide and industry updates from experts.",
      ogImage: post?.featuredImage?.node?.sourceUrl || null,
      uri: postUri,
      metaTitle:
        post?.seo?.title ||
        "Observability News, and Best Practices and Guides",
      metaDesc:
        post?.seo?.metaDesc ||
        "Read how observability can scale your cloud infrastructure, best practices, how-tos guide and industry updates from experts.",
      metaRobotsNoindex: post?.seo?.metaRobotsNoindex || "index",
      metaRobotsNofollow: post?.seo?.metaRobotsNofollow || "follow",
      faqSchema,
      ...(await serverSideTranslations(
        currentLocale,
        ["common", "header", "footer"],
        nextI18NextConfig
      )),
    },
  };
};

export const getStaticPaths = async ({ locales }) => {
  const allPosts = await getAllPostsWithSlug();
  const allLocales = locales || nextI18NextConfig.i18n.locales;

  const paths = allPosts.edges.flatMap((item) => {
    if (isLegacyBlogPostSlug(item.node.slug)) {
      return [];
    }
    return allLocales.map((locale) => ({
      params: { slug: item.node.slug },
      locale,
    }));
  });

  return {
    paths: paths,
    fallback: "blocking",
  };
};
